import PropTypes from 'prop-types';
import React from 'react';

import _ from 'underscore';

import * as FileBrowserDAO from 'file-browser/FileBrowserDAO';
import FileBrowserUtil from 'file-browser/FileBrowserUtil';
import FinderActions from 'file-browser/FinderActions';
import FinderUtil from 'file-browser/FinderUtil';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import ShortcutIcon from 'ui-library/icons/ShortcutIcon';
import Modal from 'ui-library/modals/Modal';
import {ButtonStyle} from 'ui-library/buttons/Button';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import * as ModalContainer from 'ui-library/ModalContainer';
import SortableTable from 'ui-library/SortableTable';
import * as ShortcutUtil from 'util/ShortcutUtil';

class FilesForDeleteModal extends React.Component {
  static propTypes = {
    onRequestClose: PropTypes.func.isRequired,
    fileIds: PropTypes.array.isRequired,
    doDelete: PropTypes.func.isRequired,
    // e.g., '<span>the project <strong>Foo</strong></span>' or 'these folders'
    deleteDescription: PropTypes.node,
    // should delete shortcuts (if available) instead of original files
    shouldDetectShortcuts: PropTypes.bool.isRequired,
  };

  static open(props) {
    // eslint-disable-next-line no-restricted-syntax
    const modalProps = _.extend({}, props, {
      onRequestClose: () => ModalContainer.close(FilesForDeleteModal),
    });
    ModalContainer.open(FilesForDeleteModal, React.createElement(FilesForDeleteModal, modalProps));
  }

  static openForFileDelete(props, files, finderId) {
    // eslint-disable-next-line no-restricted-syntax
    const modalProps = _.extend({}, props, {
      onRequestClose: () => ModalContainer.close(FilesForDeleteModal),
      fileIds: files.map((file) => file.id),
      doDelete: () => {
        const parentFolderIds = files.map((id) => FinderUtil.parentFolderId(id));
        FinderActions.bulkDelete(finderId, files, {parentFolderIds});
      },
      shouldDetectShortcuts: ShortcutUtil.shouldDetectShortcuts(finderId),
    });
    ModalContainer.open(FilesForDeleteModal, React.createElement(FilesForDeleteModal, modalProps));
  }

  static defaultProps = {shouldDetectShortcuts: false};

  state = {
    // filesForDelete and foldersForDelete are null when loading or when an error has occurred
    filesForDelete: null,
    foldersForDelete: null,
    requestFailed: false,
    userMessage: '',
  };

  componentDidMount() {
    FileBrowserDAO.getFilesForDelete(this.props.fileIds, this.props.shouldDetectShortcuts)
      .done((response) => {
        this.setState({
          filesForDelete: response.filesForDelete.map((file) => this._getFileProperties(file)),
          foldersForDelete: response.foldersForDelete.map((file) => this._getFileProperties(file)),
        });
      })
      .fail((err) => {
        let message =
          'Sorry, something went wrong and your delete cannot be completed.' +
          'Try deleting your items again, or contact support for assistance';
        if (err?.responseJSON?.userMessage) {
          message = `${err.responseJSON.userMessage} Please fix the issue, or contact support for assistance.`;
        }
        this.setState({requestFailed: true, userMessage: message});
      });
  }

  _hasLoaded = () => this.state.filesForDelete && this.state.foldersForDelete;

  _getFileProperties = (file) => ({
    id: file.id,
    name: file.name,
    lowercaseName: file.name.toLowerCase(),
    creatorHandle: file.creator ? file.creator.handle : '',
    projectName: file.projectName,
    lowercaseProjectName: file.projectName.toLowerCase(),
    isShortcut: file.isShortcut,
  });

  // Sort all folders before all files, and within each category sort the hidden items
  // (the items that were not directly selected for delete) last.
  _getSortedFileList = () => {
    // eslint-disable-next-line no-restricted-syntax
    const isHidden = (f) => !_.contains(this.props.fileIds, f.id);
    const folders = _.sortBy(this.state.foldersForDelete, isHidden);
    const files = _.sortBy(this.state.filesForDelete, isHidden);
    return folders.concat(files);
  };

  _handleConfirmDelete = () => {
    this.props.doDelete();
    this.props.onRequestClose();
  };

  renderFilesForDelete = () => {
    if (this.state.requestFailed) {
      return this.state.userMessage;
    } else if (!this._hasLoaded()) {
      return (
        <div className="text-center u-spacing-top u-spacing-bottom">
          <HorizontalThrobber />
        </div>
      );
    } else {
      return (
        <div>
          <p>
            Are you sure you want to delete {this.props.deleteDescription}? This action is not reversible.
          </p>
          <p>The following items and folders will be deleted:</p>
          <div className="filesForDeleteModal-table">
            <SortableTable
              columns={[
                SortableTable.defaultColumn('Type', 'id', {
                  render: (column, row, props) => {
                    const IconClass = row.isShortcut ? ShortcutIcon : BenchlingIcon;
                    return <IconClass className="faded" icon={FileBrowserUtil.getFileIconName(row)} />;
                  },
                }),
                SortableTable.defaultColumn('Name', 'lowercaseName', {
                  render: (column, row, props) => row.name,
                }),
                SortableTable.defaultColumn('Project', 'lowercaseProjectName', {
                  render: (column, row, props) => row.projectName,
                }),
                SortableTable.defaultColumn('Creator', 'creatorHandle'),
              ]}
              rows={this._getSortedFileList()}
              clickable={false}
              selectable={false}
              hoverable={true}
              coloredHeader={true}
              wrapCell={true}
            />
          </div>
        </div>
      );
    }
  };

  render() {
    return (
      <Modal
        title="Confirm delete"
        onRequestClose={this.props.onRequestClose}
        buttons={[
          {
            text: 'Permanently delete',
            onClick: this._handleConfirmDelete,
            disabled: !this._hasLoaded(),
            style: ButtonStyle.NEGATIVE,
          },
        ]}
      >
        {this.renderFilesForDelete()}
      </Modal>
    );
  }
}

export default FilesForDeleteModal;
